import { Checkbox } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { SearchIconStroke } from '../../../../../../../assets/icons/common/common-icons';
import { BaseModal } from '../../../../../../../common/components/BaseModal/BaseModal';
import { CommonBadge } from '../../../../../../../common/components/CommonBadge/CommonBadge';
import { ItemLogo } from '../../../../../../../common/components/ItemLogo/ItemLogo';
import { navigateToUrl } from '../../../../../../../common/utils/navigation.utils';
import { getUrlDisplayName } from '../../../../../../../common/utils/string.utils';
import { useStore } from '../../../../../../app/data/root.store';
import { ItemMoreInfos, MoreInfosItem } from '../../../../../../wealth/pages/class/components/ClassPage.styles';
import { getDocTypeIcon } from '../../../../../assets/docsTypes/docTypes';
import { getAllLinkableItems } from '../../../../../utils/linkable-items.utils';
import { CheckboxWrapper, CloseButton, EmptyState, FileIcon, FileName, ItemContent, ItemCount, ItemDetails, ItemName, ItemNameText, ItemRow, ItemsList, ItemsSection, LinkedItemModalWrapper, ModalBody, ModalHeader, ModalTitle, SaveButton, SaveButtonContainer, SearchIcon, SearchInput, SearchInputWrapper, SearchSection } from './LinkedItemModal.styles';

export const LinkedItemModal = observer(({
    isOpen,
    onClose,
    fileName,
    selectedItems = [],
    onSave
}) => {
    const { metadataStore, vaultStore, createItemStore } = useStore();
    const history = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssetIds, setSelectedAssetIds] = useState(new Set(selectedItems.map(item => item.id)));
    const [initiallySelectedIds, setInitiallySelectedIds] = useState(new Set(selectedItems.map(item => item.id)));

    useEffect(() => {
        if (isOpen) {
            const newSelectedIds = new Set(selectedItems.map(item => item.id));
            setSelectedAssetIds(newSelectedIds);
            setInitiallySelectedIds(newSelectedIds);
            setSearchTerm('');

            if (!metadataStore.isLoaded && !metadataStore.isLoading) {
                metadataStore.fetchData();
            }
        }
    }, [isOpen, selectedItems, metadataStore]);

    const allAssets = useMemo(() => {
        return getAllLinkableItems(metadataStore, initiallySelectedIds);
    }, [metadataStore, initiallySelectedIds]);

    const filteredAssets = useMemo(() => {
        if (!allAssets.length) return [];

        const assetsWithStatus = allAssets.map(asset => ({
            ...asset,
            wasSelected: initiallySelectedIds.has(asset.id)
        }));

        return assetsWithStatus.sort((a, b) => {
            if (a.wasSelected && !b.wasSelected) return -1;
            if (!a.wasSelected && b.wasSelected) return 1;

            const aTitle = a.title || a.name || '';
            const bTitle = b.title || b.name || '';

            if (searchTerm && searchTerm.trim()) {
                const aTitleIndex = aTitle.toLowerCase().indexOf(searchTerm.toLowerCase());
                const bTitleIndex = bTitle.toLowerCase().indexOf(searchTerm.toLowerCase());

                // If both have the search term, sort by position (earlier positions first)
                if (aTitleIndex !== -1 && bTitleIndex !== -1) {
                    return aTitleIndex - bTitleIndex;
                }

                // If only one has the search term, that one comes first
                if (aTitleIndex !== -1 && bTitleIndex === -1) return -1;
                if (aTitleIndex === -1 && bTitleIndex !== -1) return 1;

                // If neither has the search term (fuzzy match only), keep original order
                return 0;
            }

            // When no search term, sort alphabetically
            return aTitle.toLowerCase().localeCompare(bTitle.toLowerCase());
        });
    }, [allAssets, searchTerm, initiallySelectedIds]);

    const handleItemToggle = (assetId) => {
        const newSelectedIds = new Set(selectedAssetIds);
        if (newSelectedIds.has(assetId)) {
            newSelectedIds.delete(assetId);
        } else {
            newSelectedIds.add(assetId);
        }
        setSelectedAssetIds(newSelectedIds);
    };

    const handleItemNavigation = (asset, event) => {
        event.stopPropagation();

        const classTitle = asset.categoryClass?.title || (asset.type === 'liability' ? 'Liabilities' : 'Unknown');
        const urlPath = `/wealth/assets/${getUrlDisplayName(classTitle)}/${asset.id}/vault`;
        onClose();
        vaultStore.clearSelectedFile();

        if ('in process' === asset.llmProcessingStatus) {
            createItemStore.openModalForSpecificCategory(asset.classId, asset.categoryId, { title: asset.llmData.title, name: asset.llmData.name, value: +asset.value, cashInvested: +asset.value, currency: asset.currency, oldAssetId: asset.id, llmProcessingStatus: 'done', isMagicBoxAi: true, remarks: 'Automatically generated from an uploaded file by AI.' });
        } else {
            navigateToUrl(history, urlPath);
        }
    };

    const handleSave = () => {
        const selectedAssets = allAssets.filter(asset => selectedAssetIds.has(asset.id));
        onSave(selectedAssets);
        onClose();
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return getDocTypeIcon();
        const extension = fileName.split('.').pop().toLowerCase();
        return getDocTypeIcon(extension);
    };

    const renderAssetItem = (asset) => {
        const isSelected = selectedAssetIds.has(asset.id);
        const displayName = asset.title || asset.name || 'Unnamed Asset';

        return (
            <ItemRow
                key={asset.id}
                onClick={() => handleItemToggle(asset.id)}
            >
                <ItemContent>
                    <ItemLogo item={asset} customStyles={{ marginRight: '0px' }} />
                    <ItemDetails>
                        <ItemName
                            onClick={(event) => handleItemNavigation(asset, event)}
                            style={{ cursor: 'pointer' }}
                        >
                            <ItemNameText>{displayName}</ItemNameText>
                            {asset.isClosed && <CommonBadge badgeType="closed" noMargin />}
                            {asset.connectedProvider === 'vip' && <CommonBadge badgeType="vipService" noMargin />}
                            {asset.ownershipPercentage && (
                                <CommonBadge
                                    title={`${asset.ownershipPercentage}%`}
                                    badgeWrapperStyle={{
                                        background: '#DDE3EB',
                                        color: '#585D66',
                                        borderRadius: '2px 0px 0px 2px',
                                        padding: '1px 1px 0px 4px',
                                        height: '16px'
                                    }}
                                    noMargin
                                />
                            )}
                        </ItemName>
                        <ItemMoreInfos>
                            <MoreInfosItem>
                                {asset.category?.title || 'Unknown'}
                            </MoreInfosItem>
                        </ItemMoreInfos>
                    </ItemDetails>
                </ItemContent>
                <CheckboxWrapper data-checkbox-wrapper>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => handleItemToggle(asset.id)}
                        onClick={(event) => event.stopPropagation()}
                    />
                </CheckboxWrapper>
            </ItemRow>
        );
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <BaseModal width="500px" height="636px" borderRadius={12} onBackDropClick={onClose}>
            <LinkedItemModalWrapper>
                <CloseButton onClick={onClose}>×</CloseButton>

                <ModalHeader>
                    <ModalTitle>Linked item</ModalTitle>
                    <ItemCount>{selectedAssetIds.size}</ItemCount>
                    <div style={{ width: '1px', height: '32px', background: '#DDE3EB' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FileIcon>
                            {getFileIcon(fileName)}
                        </FileIcon>
                        <FileName>{fileName}</FileName>
                    </div>
                </ModalHeader>

                <ModalBody>
                    <SearchSection>
                        <p>Attach this file to portfolio items (assets or liabilities) to display it on their pages.</p>
                        <SearchInputWrapper>
                            <SearchInput
                                type="text"
                                placeholder="Search asset / liability"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <SearchIcon><SearchIconStroke /></SearchIcon>
                        </SearchInputWrapper>
                    </SearchSection>

                    <ItemsSection>

                        {filteredAssets.length === 0 ? (
                            <EmptyState>
                                {searchTerm ? 'No assets found matching your search.' : 'No assets available.'}
                            </EmptyState>
                        ) : (
                            <ItemsList>
                                {filteredAssets.map(renderAssetItem)}
                            </ItemsList>
                        )}
                    </ItemsSection>
                </ModalBody>

                <SaveButtonContainer>
                    <SaveButton onClick={handleSave}>
                        Save linked items
                    </SaveButton>
                </SaveButtonContainer>
            </LinkedItemModalWrapper>
        </BaseModal>,
        document.body
    );
}); 